import Coupon from 'common/components/entities/OfferPriceNew/Coupon'
import {
  PricePlanProps,
  ReadablePricePlanAmount,
} from 'common/components/entities/OfferPriceNew/PricePlan'
import Total from 'common/components/entities/OfferPriceNew/Total'
import CurrencyBadgeUi from 'common/components/entities/OfferPriceNew/ui/CurrencyBadgeUi'
import PricePlanAmountUi from 'common/components/entities/OfferPriceNew/ui/PricePlanAmountUi'
import PricePlanItemUi from 'common/components/entities/OfferPriceNew/ui/PricePlanItemUi'
import PricePlanNameUi from 'common/components/entities/OfferPriceNew/ui/PricePlanNameUi'
import PricePlanUi from 'common/components/entities/OfferPriceNew/ui/PricePlanUi'
import Vat from 'common/components/entities/OfferPriceNew/Vat'
import { MakeOptional } from 'common/types/utilityTypes'
import React from 'react'

export interface BumpPricePlanProps
  extends MakeOptional<
    PricePlanProps,
    'descriptionFontSize' | 'descriptionLineHeight' | 'descriptionColor'
  > {
  skipLeftOffset?: boolean
}

const BumpPricePlan = ({
  plan,
  pricePlanPricing,
  coupon,
  locale,
  nameFontSize,
  mobileNameFontSize,
  nameFontStyle,
  mobileNameFontStyle,
  nameFontWeight,
  mobileNameFontWeight,
  nameFontFamily,
  mobileNameFontFamily,
  nameLineHeight,
  mobileNameLineHeight,
  nameColor,
  mobileNameColor,
  amountFontSize,
  mobileAmountFontSize,
  amountFontStyle,
  mobileAmountFontStyle,
  amountFontWeight,
  mobileAmountFontWeight,
  amountFontFamily,
  mobileAmountFontFamily,
  amountLineHeight,
  mobileAmountLineHeight,
  amountColor,
  mobileAmountColor,
  dataTestId,
  skipLeftOffset = false,
}: BumpPricePlanProps) => {
  const needTotal = pricePlanPricing.tax.amount > 0 || !!coupon

  return (
    <PricePlanUi>
      <PricePlanItemUi skipLeftOffset={skipLeftOffset}>
        <PricePlanNameUi
          fontSize={nameFontSize}
          mobileFontSize={mobileNameFontSize}
          fontStyle={nameFontStyle}
          mobileFontStyle={mobileNameFontStyle}
          fontWeight={nameFontWeight}
          mobileFontWeight={mobileNameFontWeight}
          fontFamily={nameFontFamily}
          mobileFontFamily={mobileNameFontFamily}
          lineHeight={nameLineHeight}
          mobileLineHeight={mobileNameLineHeight}
          color={nameColor}
          mobileColor={mobileNameColor}
          data-test-id={dataTestId}
        >
          {plan.name}
        </PricePlanNameUi>
        <PricePlanAmountUi
          fontSize={amountFontSize}
          mobileFontSize={mobileAmountFontSize}
          fontStyle={amountFontStyle}
          mobileFontStyle={mobileAmountFontStyle}
          fontWeight={amountFontWeight}
          mobileFontWeight={mobileAmountFontWeight}
          fontFamily={amountFontFamily}
          mobileFontFamily={mobileAmountFontFamily}
          lineHeight={amountLineHeight}
          mobileLineHeight={mobileAmountLineHeight}
          color={amountColor}
          mobileColor={mobileAmountColor}
        >
          <CurrencyBadgeUi color={amountColor} mobileColor={mobileAmountColor}>
            {plan.currency}
          </CurrencyBadgeUi>
          <ReadablePricePlanAmount
            plan={plan}
            amount={pricePlanPricing.originalAmount}
            locale={locale}
          />
        </PricePlanAmountUi>
      </PricePlanItemUi>
      {coupon && (
        <Coupon
          amountFontSize={amountFontSize}
          mobileAmountFontSize={mobileAmountFontSize}
          amountFontStyle={amountFontStyle}
          mobileAmountFontStyle={mobileAmountFontStyle}
          amountFontWeight={amountFontWeight}
          mobileAmountFontWeight={mobileAmountFontWeight}
          amountFontFamily={amountFontFamily}
          mobileAmountFontFamily={mobileAmountFontFamily}
          amountLineHeight={amountLineHeight}
          mobileAmountLineHeight={mobileAmountLineHeight}
          amountColor={amountColor}
          mobileAmountColor={mobileAmountColor}
          fontSize={nameFontSize}
          mobileFontSize={mobileNameFontSize}
          fontStyle={nameFontStyle}
          mobileFontStyle={mobileNameFontStyle}
          fontWeight={nameFontWeight}
          mobileFontWeight={mobileNameFontWeight}
          fontFamily={nameFontFamily}
          mobileFontFamily={mobileNameFontFamily}
          lineHeight={nameLineHeight}
          mobileLineHeight={mobileNameLineHeight}
          color={nameColor}
          mobileColor={mobileNameColor}
          coupon={coupon}
          discountAmount={pricePlanPricing.discount.amount}
          locale={locale}
        />
      )}
      {pricePlanPricing.tax.amount > 0 && (
        <Vat
          plan={plan}
          skipLeftOffset={skipLeftOffset}
          quantity={1}
          taxAmount={pricePlanPricing.tax.amount}
          taxPercent={pricePlanPricing.tax.rate}
          locale={locale}
          amountFontSize={amountFontSize}
          mobileAmountFontSize={mobileAmountFontSize}
          amountFontStyle={amountFontStyle}
          mobileAmountFontStyle={mobileAmountFontStyle}
          amountFontWeight={amountFontWeight}
          mobileAmountFontWeight={mobileAmountFontWeight}
          amountFontFamily={amountFontFamily}
          mobileAmountFontFamily={mobileAmountFontFamily}
          amountLineHeight={amountLineHeight}
          mobileAmountLineHeight={mobileAmountLineHeight}
          amountColor={amountColor}
          mobileAmountColor={mobileAmountColor}
          fontSize={nameFontSize}
          mobileFontSize={mobileNameFontSize}
          fontStyle={nameFontStyle}
          mobileFontStyle={mobileNameFontStyle}
          fontWeight={nameFontWeight}
          mobileFontWeight={mobileNameFontWeight}
          fontFamily={nameFontFamily}
          mobileFontFamily={mobileNameFontFamily}
          lineHeight={nameLineHeight}
          mobileLineHeight={mobileNameLineHeight}
          color={nameColor}
          mobileColor={mobileNameColor}
        />
      )}
      {needTotal && (
        <Total
          skipLeftOffset={skipLeftOffset}
          coupon={coupon}
          plan={plan}
          quantity={1}
          grossAmount={pricePlanPricing.grossAmount}
          locale={locale}
          amountFontSize={amountFontSize}
          mobileAmountFontSize={mobileAmountFontSize}
          amountFontStyle={amountFontStyle}
          mobileAmountFontStyle={mobileAmountFontStyle}
          amountFontWeight={amountFontWeight}
          mobileAmountFontWeight={mobileAmountFontWeight}
          amountFontFamily={amountFontFamily}
          mobileAmountFontFamily={mobileAmountFontFamily}
          amountLineHeight={amountLineHeight}
          mobileAmountLineHeight={mobileAmountLineHeight}
          amountColor={amountColor}
          mobileAmountColor={mobileAmountColor}
          fontSize={nameFontSize}
          mobileFontSize={mobileNameFontSize}
          fontStyle={nameFontStyle}
          mobileFontStyle={mobileNameFontStyle}
          fontWeight={nameFontWeight}
          mobileFontWeight={mobileNameFontWeight}
          fontFamily={nameFontFamily}
          mobileFontFamily={mobileNameFontFamily}
          lineHeight={nameLineHeight}
          mobileLineHeight={mobileNameLineHeight}
          color={nameColor}
          mobileColor={mobileNameColor}
        />
      )}
    </PricePlanUi>
  )
}

export default BumpPricePlan
